import { useState, useEffect } from "react";
import { Warning, CheckCircle, Spinner } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const KEY = "mpc_age_consent_v1";

export default function AgeConsentGate() {
  const { login, register, formatApiErrorDetail } = useAuth();
  const [open, setOpen] = useState(false);
  const [age, setAge] = useState(false);
  const [terms, setTerms] = useState(false);
  const [mode, setMode] = useState("guest");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(KEY) !== "true") setOpen(true);
  }, []);

  const canEnter = age && terms;

  const persistConsent = () => {
    localStorage.setItem(KEY, "true");
    localStorage.setItem(`${KEY}_at`, new Date().toISOString());
  };

  const accept = () => {
    if (!canEnter) return;
    persistConsent();
    setOpen(false);
  };

  const submitAuth = async () => {
    if (!canEnter) { toast.error("Please confirm the two boxes above to continue"); return; }
    if (!email || !password || (mode === "register" && !name)) {
      toast.error("Please fill in all fields");
      return;
    }
    setBusy(true);
    try {
      if (mode === "register") await register(name, email, password);
      else await login(email, password);
      persistConsent();
      toast.success(mode === "register" ? "Account created — welcome!" : "Signed in — welcome back!");
      setOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail ? formatApiErrorDetail(e) : (e.response?.data?.detail || "Something went wrong"));
    } finally {
      setBusy(false);
    }
  };

  const decline = () => {
    window.location.href = "https://www.google.com";
  };

  if (!open) return null;

  const Check = ({ checked, onChange, testid, children }) => (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className="w-full flex items-start gap-3 text-left border border-black/10 p-4 hover:border-primary/40 transition-colors"
      style={{ transition: "border-color 0.2s" }}
    >
      <span className={`shrink-0 mt-0.5 w-5 h-5 border grid place-items-center ${checked ? "bg-primary border-primary" : "border-black/30"}`} style={{ transition: "background-color 0.2s, border-color 0.2s" }}>
        {checked && <CheckCircle size={14} weight="bold" className="text-black" />}
      </span>
      <span className="text-sm text-muted-foreground leading-relaxed">{children}</span>
    </button>
  );

  const Tab = ({ id, label }) => (
    <button
      type="button"
      data-testid={`gate-tab-${id}`}
      onClick={() => setMode(id)}
      className={`flex-1 label-mono py-2.5 border-b-2 transition-colors ${mode === id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
      style={{ transition: "color 0.2s, border-color 0.2s" }}
    >
      {label}
    </button>
  );

  const inputCls = "w-full bg-white border border-black/15 px-4 py-3 font-mono text-sm text-black focus:border-primary outline-none transition-colors";

  return (
    <div className="fixed inset-0 z-[200] bg-black/90 backdrop-blur-md grid place-items-center p-4 overflow-y-auto">
      <div data-testid="age-consent-modal" className="w-full max-w-lg bg-surface border border-black/10 p-8 my-8 fade-up">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo_mark.png" alt="Mulan Peptide Collection" className="h-12 w-12 object-contain" />
          <div className="leading-none">
            <span className="font-head font-semibold text-lg tracking-[0.12em] text-primary">MULAN PEPTIDE COLLECTION</span>
            <span className="block label-mono text-[0.55rem] text-muted-foreground mt-1">Age Verification & Consent</span>
          </div>
        </div>

        <div className="flex gap-3 border border-primary/20 bg-primary/5 p-4 mb-6">
          <Warning size={22} className="text-primary shrink-0 mt-0.5" />
          <p className="text-xs text-muted-foreground leading-relaxed">
            All products are sold strictly for <span className="text-primary">laboratory and research use only</span>. They are not for diagnostic or therapeutic use. You must confirm the following to enter.
          </p>
        </div>

        <div className="space-y-3 mb-6">
          <Check checked={age} onChange={setAge} testid="consent-age">
            I confirm that I am <span className="text-foreground">at least 21 years of age</span> (or the legal age of majority in my jurisdiction).
          </Check>
          <Check checked={terms} onChange={setTerms} testid="consent-terms">
            I agree to the <span className="text-foreground">Terms of Sale</span> and assume full responsibility for the lawful handling of these compounds.
          </Check>
        </div>

        <div className="flex border-b border-black/10 mb-5">
          <Tab id="guest" label="Guest" />
          <Tab id="signin" label="Sign In" />
          <Tab id="register" label="Register" />
        </div>

        {mode === "guest" && (
          <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
            Continue without an account — you can still check out as a guest. Or <span className="text-foreground">Sign In / Register</span> to track your orders.
          </p>
        )}

        {mode === "signin" && (
          <div className="space-y-3 mb-5">
            <input data-testid="gate-signin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} />
            <input data-testid="gate-signin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} />
          </div>
        )}

        {mode === "register" && (
          <div className="space-y-3 mb-5">
            <p data-testid="register-benefits" className="text-xs text-primary/90 leading-relaxed border-l-2 border-primary pl-3">
              Create an account to track your orders and reorder your favorites faster.
            </p>
            <input data-testid="gate-register-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} />
            <input data-testid="gate-register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} />
            <input data-testid="gate-register-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 6 characters)" className={inputCls} />
          </div>
        )}

        {!canEnter && (
          <p className="text-[0.65rem] text-muted-foreground mb-3">Confirm the two boxes above to continue.</p>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          {mode === "guest" ? (
            <button
              data-testid="consent-enter"
              onClick={accept}
              disabled={!canEnter}
              className="flex-1 bg-primary text-black label-mono py-4 hover:bg-[#E6C766] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ transition: "background-color 0.2s, opacity 0.2s" }}
            >
              I Agree — Enter Site
            </button>
          ) : (
            <button
              data-testid={mode === "register" ? "gate-register-submit" : "gate-signin-submit"}
              onClick={submitAuth}
              disabled={!canEnter || busy}
              className="flex-1 flex items-center justify-center gap-2 bg-primary text-black label-mono py-4 hover:bg-[#E6C766] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ transition: "background-color 0.2s, opacity 0.2s" }}
            >
              {busy ? <><Spinner size={16} className="animate-spin" /> Please wait…</> : (mode === "register" ? "Create Account & Enter" : "Sign In & Enter")}
            </button>
          )}
          <button
            data-testid="consent-decline"
            onClick={decline}
            className="flex-1 border border-black/15 label-mono py-4 text-muted-foreground hover:border-destructive hover:text-destructive transition-colors"
            style={{ transition: "color 0.2s, border-color 0.2s" }}
          >
            Decline & Exit
          </button>
        </div>
      </div>
    </div>
  );
}
